import { rowSpan } from "./board";
import type { LaddersJump, LaddersMoveEvent, LaddersRollEvent } from "./types";

/* ---- THE MOTION CONTRACT -----------------------------------------------------------------
 * ONE set of numbers for two sides: the server holds the next BOT action for these beats
 * (`laddersEngine.postMoveDelayMs`, batch A2) and the web animates the same events with the
 * same per-phase constants (batch W1). Shorten one side alone and a bot acts while the last
 * piece is still sliding. The web imports these; it never restates them.
 *
 * ROUND 2 — THE TWO-PHASE TURN (roll, then move), paced the way منچ paces its bots (`lastAction`:
 * a THROW holds for the die, a MOVE holds for the motion), with this game's own numbers:
 *  - a THROW (`rollHoldMs`): the die tumbles (ROLL_LAND_MS) → a short read (ROLL_READ_MS). On an
 *    OVERSHOOT the throw is the whole step: + OVERSHOOT_HOLD_MS («you need exactly N») +
 *    MOVE_SETTLE_MS before the next throw;
 *  - a MOVE (`moveBeatMs`): the walk, one cell per pip (STEP_HOP_MS each) → the climb or slide
 *    (jumpMs) → the hit pieces sliding home together (CAPTURE_HOME_MS, only when something was
 *    hit) → a settle (MOVE_SETTLE_MS, or WIN_SETTLE_MS on 100).
 * Every one of these is a CSS transition / finite WAAPI animation while it runs and nothing at
 * all afterwards. A HUMAN's window is never held by these: it arms on the state change, as منچ's.
 * ------------------------------------------------------------------------------------------ */

/**
 * The die's tumble. Must be ≥ the web's shared die throw (`DIE_THROW_MS`,
 * apps/web/src/game/backgammon-dice.tsx — 1000 today); a web test pins that.
 */
export const ROLL_LAND_MS = 1000;
/** After the die lands, before the piece moves (a bot's) — time to read the number. */
export const ROLL_READ_MS = 350;
/** Per cell walked (منچ's own `STEP_HOP_MS`). */
export const STEP_HOP_MS = 190;
/** A climb or slide: a base… */
export const JUMP_BASE_MS = 450;
/** …plus this per row it spans, so a long snake is a longer slide than a short one. */
export const JUMP_PER_ROW_MS = 110;
/**
 * A hit piece slides from its cell back to the start pad (round 2's capture). Every piece hit by
 * one move slides AT ONCE, so one move costs this once, whatever the count.
 */
export const CAPTURE_HOME_MS = 650;
/** After an ordinary move, before the next throw. */
export const MOVE_SETTLE_MS = 600;
/** An overshoot moves nothing; this is the beat that says so (in place of the move phase). */
export const OVERSHOOT_HOLD_MS = 700;
/** After the winning move, before the end card. */
export const WIN_SETTLE_MS = 1500;

/** How long a climb or slide runs. */
export function jumpMs(jump: LaddersJump): number {
  return JUMP_BASE_MS + JUMP_PER_ROW_MS * rowSpan(jump.from, jump.to);
}

/** The walk alone (no jump, no capture, no settle). */
export function walkMs(move: LaddersMoveEvent): number {
  return move.path.length * STEP_HOP_MS;
}

/**
 * The beat of one THROW — what the server holds a bot after it threw, before its next action:
 * the tumble + the read; an overshoot adds its hold and the settle (it ends the step).
 */
export function rollHoldMs(roll: LaddersRollEvent): number {
  return ROLL_LAND_MS + ROLL_READ_MS + (roll.overshoot ? OVERSHOOT_HOLD_MS + MOVE_SETTLE_MS : 0);
}

/** The beat of one MOVE — what the server holds the next bot action after it. */
export function moveBeatMs(move: LaddersMoveEvent): number {
  return (
    walkMs(move) +
    (move.jump ? jumpMs(move.jump) : 0) +
    (move.captured.length > 0 ? CAPTURE_HOME_MS : 0) +
    (move.won ? WIN_SETTLE_MS : MOVE_SETTLE_MS)
  );
}
